import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import FileViewer from 'ats/components/file-viewer/FileViewer';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import { openApplicantProfile } from 'ats/utils/openEntityProfile';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { externalApplicationStore } from './ExternalApplicationStore';
import ExternalApplicationAssignModal from './components/ExternalApplicationAssignModal';
import ExternalApplicationDecisionModal from './components/ExternalApplicationDecisionModal';
import {
	EXTERNAL_APPLICATION_APPROVE_PATHS,
	getExternalApplicationJobId,
	getExternalApplicationJobTitle,
	getExternalApplicationRoleName,
	getExternalApplicationSourceChipLabel,
	getExternalApplicationStatusLabel
} from '../open-jobs/externalApplicationOptions';

function getHeadline(data) {
	const role =
		getExternalApplicationRoleName(data?.job) ||
		getExternalApplicationJobTitle(data?.job);
	const client = data?.job?.client?.name;
	if (role && client) return `${role} - ${client}`;
	return role || '';
}

const PDF_HEADER = [0x25, 0x50, 0x44, 0x46];
const DOCX_MIME =
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

async function blobStartsWithPdfHeader(blob) {
	const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
	return PDF_HEADER.every((byte, index) => header[index] === byte);
}

async function blobLooksLikeDocx(blob) {
	const header = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
	if (header[0] !== 0x50 || header[1] !== 0x4b) return false;
	const bytes = new Uint8Array(await blob.arrayBuffer());
	const marker = 'word/document.xml';
	const text = new TextDecoder('latin1').decode(bytes);
	return text.includes(marker);
}

async function classifyResumeBlob(blob) {
	const type = (blob.type || '').toLowerCase().split(';')[0].trim();
	if (type === 'application/pdf' && (await blobStartsWithPdfHeader(blob))) {
		return 'application/pdf';
	}
	if (type === DOCX_MIME && (await blobLooksLikeDocx(blob))) {
		return DOCX_MIME;
	}
	return null;
}

function getAppliedToLabel(job) {
	const title = getExternalApplicationJobTitle(job);
	const client = job?.client?.name;
	const jobId = getExternalApplicationJobId(job);
	return [title, client, jobId].filter(Boolean).join(' - ') || '—';
}

export default function ExternalApplicationProfile({
	applicationId,
	onClose,
	onSuccess,
	onDecision
}) {
	const [data, setData] = useState(null);
	const [resumeUrl, setResumeUrl] = useState(null);
	const [resumeMimeType, setResumeMimeType] = useState(null);
	const [resumeState, setResumeState] = useState({
		loading: false,
		error: null
	});
	const [deciding, setDeciding] = useState(false);

	const loadData = async id => {
		const result = await externalApplicationStore.get(id);
		if (result?.status === 'success') {
			setData(result.data);
			return result.data;
		}
		notificationStore.pushErrorNotification(
			'Error',
			result?.message || 'Failed to load application'
		);
		return null;
	};

	const loadResume = useCallback(async (id, application) => {
		if (!application?.hasResume) {
			setResumeUrl(null);
			setResumeMimeType(null);
			setResumeState({ loading: false, error: 'missing' });
			return;
		}

		setResumeState({ loading: true, error: null });
		try {
			const result = await externalApplicationStore.downloadResume(id);
			const isBlob = result instanceof Blob;
			const contentType =
				isBlob && result && result.status !== 'fail'
					? await classifyResumeBlob(result)
					: null;
			if (!contentType) {
				setResumeUrl(null);
				setResumeMimeType(null);
				setResumeState({ loading: false, error: 'failed' });
				return;
			}

			const resumeBlob = new Blob([result], { type: contentType });
			const url = URL.createObjectURL(resumeBlob);
			setResumeUrl(previous => {
				if (previous) URL.revokeObjectURL(previous);
				return url;
			});
			setResumeMimeType(contentType);
			setResumeState({ loading: false, error: null });
		} catch (error) {
			setResumeUrl(null);
			setResumeState({ loading: false, error: 'failed' });
		}
	}, []);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			const application = await loadData(applicationId);
			if (cancelled || !application) return;
			await loadResume(applicationId, application);
		};

		load();

		return () => {
			cancelled = true;
		};
	}, [applicationId, loadResume]);

	useEffect(() => {
		return () => {
			if (resumeUrl) {
				URL.revokeObjectURL(resumeUrl);
			}
		};
	}, [resumeUrl]);

	const isPending = data?.status === 'pending';
	const linkedinUrl = data?.linkedinUrl;
	const job = data?.job;
	const dedupApplicants = data?.dedup?.applicants || [];
	const hasDedupMatch =
		data?.dedup?.result === 'linked' || data?.dedup?.result === 'conflict';
	const initial = (data?.name || '?').trim().charAt(0).toUpperCase();
	const headline = getHeadline(data);
	const applicantName = data?.name || 'this applicant';
	const hasEmail = Boolean(data?.email?.trim());

	const showDecisionToast = result => {
		const applicantId = result?.data?.applicant?.id;
		notificationStore.pushNotification({
			type: notificationStore.SUCCESS_NOTIFICATION,
			title: 'Application approved',
			message: 'The applicant is now a candidate.',
			action: applicantId
				? {
						label: 'See Profile',
						href: `/ats/candidates/${applicantId}`
					}
				: undefined
		});
	};

	const handleApprove = async (approvePath, jobIds, { sendEmail } = {}) => {
		if (deciding || !isPending) return false;
		setDeciding(true);
		try {
			const result = await externalApplicationStore.approve(applicationId, {
				approvePath,
				jobIds,
				sendEmail: Boolean(sendEmail)
			});

			if (result.status === 'success') {
				if (result.data?.skippedJobs?.length) {
					const skipped = result.data.skippedJobs
						.map(jobItem => jobItem.title)
						.join(', ');
					notificationStore.pushNotification({
						type: notificationStore.ERROR_NOTIFICATION,
						title: 'Some jobs were skipped',
						message: `This candidate already has an application on: ${skipped}.`
					});
				}
				const application = await loadData(applicationId);
				await loadResume(applicationId, application);
				showDecisionToast(result);
				onSuccess?.();
				if (onDecision) {
					await onDecision(result.data);
				}
				setDeciding(false);
				return true;
			}

			if (result.code === 'dedup_conflict') {
				notificationStore.pushNotification({
					type: notificationStore.ERROR_NOTIFICATION,
					title: 'Matching candidates found',
					message:
						result.message ||
						'This application matches two different candidates.'
				});
				loadData(applicationId);
			} else {
				notificationStore.pushErrorNotification(
					'Approval failed',
					result.message || 'Failed to approve this application.'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Approval failed',
				'Failed to approve this application.'
			);
		}
		setDeciding(false);
		return false;
	};

	const openAssignModal = () => {
		modalStore.openModal(
			{
				title: 'Assign to other Job(s)',
				size: 'md',
				position: 'center',
				saveButtonLabel: 'Approve'
			},
			<ExternalApplicationAssignModal
				hasEmail={hasEmail}
				onApprove={(jobIds, options) =>
					handleApprove(
						EXTERNAL_APPLICATION_APPROVE_PATHS.OTHER_JOBS,
						jobIds,
						options
					)
				}
			/>
		);
	};

	const openApproveConfirmModal = (approvePath, message) => {
		modalStore.openModal(
			{
				isV2: true,
				size: 'md',
				position: 'center'
			},
			<ExternalApplicationDecisionModal
				variant="approve"
				title="Approve Application"
				message={message}
				confirmLabel="Approve"
				emailLabel="Send approval email to the applicant"
				hasEmail={hasEmail}
				onConfirm={async options => {
					await handleApprove(approvePath, [], options);
				}}
			/>
		);
	};

	const openRejectModal = () => {
		modalStore.openModal(
			{
				isV2: true,
				size: 'md',
				position: 'center'
			},
			<ExternalApplicationDecisionModal
				variant="reject"
				title="Reject Application"
				message={
					<>
						Are you sure you want to reject {applicantName}&apos;s application?
						<br />
						<br />
						<strong>Once rejected, this can&apos;t be undone.</strong>
					</>
				}
				confirmLabel="Reject"
				emailLabel="Send rejection email to the applicant"
				hasEmail={hasEmail}
				onConfirm={async ({ sendEmail }) => {
					if (deciding || !isPending) return;
					setDeciding(true);
					try {
						const result = await externalApplicationStore.reject(
							applicationId,
							{
								sendEmail
							}
						);
						if (result.status === 'success') {
							notificationStore.pushSuccessNotification(
								'Application rejected',
								`${data?.name || 'The applicant'} has been rejected.`
							);
							const application = await loadData(applicationId);
							await loadResume(applicationId, application);
							onSuccess?.();
							if (onDecision) {
								await onDecision(result.data);
							}
						} else {
							notificationStore.pushErrorNotification(
								'Rejection failed',
								result.message || 'Failed to reject this application.'
							);
						}
					} catch (error) {
						notificationStore.pushErrorNotification(
							'Rejection failed',
							'Failed to reject this application.'
						);
					}
					setDeciding(false);
				}}
			/>
		);
	};

	const approveMenuItems = [
		{
			label: 'Approve & keep in current Job',
			img: '/ats/icons/external-application-approve-check.svg',
			onClick: () =>
				openApproveConfirmModal(
					EXTERNAL_APPLICATION_APPROVE_PATHS.CURRENT_JOB,
					<>
						Are you sure you want to approve {applicantName}&apos;s application
						and keep it in <strong>{getAppliedToLabel(job)}</strong>?
					</>
				)
		},
		{
			label: 'Approve & assign to other Job(s)',
			img: '/ats/icons/external-application-approve-assign.svg',
			onClick: openAssignModal
		},
		{
			label: 'Approve without assigning to a Job',
			img: '/ats/icons/external-application-approve-no-job.svg',
			onClick: () =>
				openApproveConfirmModal(
					EXTERNAL_APPLICATION_APPROVE_PATHS.NO_JOB,
					<>
						Are you sure you want to approve {applicantName}&apos;s application
						without assigning it to a Job?
					</>
				)
		}
	];

	return (
		<div className="ExternalApplicationProfile">
			<div className="drawer-header">
				<div className="external-application-drawer-header-details">
					<div className="application-source">
						<span>Source:</span>
						<span className="application-source-value">
							{getExternalApplicationSourceChipLabel(data?.source)}
						</span>
					</div>
					{data?.referrerUser ? (
						<div className="application-referrer">
							<span>Referred by:</span>
							<UserProfileAvatar
								name={data.referrerUser.name}
								imageUrl={data.referrerUser.imageUrl}
								showName
							/>
						</div>
					) : null}
					<div className="application-applied">
						<span>Applied:</span>
						<span>
							{data?.appliedAt || data?.createdAt
								? moment(data.appliedAt || data.createdAt).format('ll')
								: 'N/A'}
						</span>
					</div>
				</div>
				<button
					className="drawer-close-btn button-bg-soft-small is-icon-only"
					onClick={() => (onClose ? onClose() : drawerStore.closeDrawer())}>
					<img src="/ats/icons/close-blue-icon.svg" alt="" />
				</button>
			</div>

			{!isPending && data?.status ? (
				<div className="application-decision-meta">
					<span className={`external-application-status is-${data.status}`}>
						{getExternalApplicationStatusLabel(data.status)}
					</span>
					{data.decidedByUser ? (
						<UserProfileAvatar
							name={data.decidedByUser.name}
							imageUrl={data.decidedByUser.imageUrl}
						/>
					) : null}
					{data.decidedAt ? (
						<span>{moment(data.decidedAt).format('ll')}</span>
					) : null}
				</div>
			) : null}

			<div className="external-application-profile-header">
				<div className="letter-avatar" aria-hidden="true">
					{initial}
				</div>
				<div className="applicant-identity">
					<h3>{data?.name || ' '}</h3>
					{headline ? <p className="headline">{headline}</p> : null}
					{data?.country?.name ? (
						<p className="location">
							<img src="/ats/icons/external-application-location.svg" alt="" />
							<span>{data.country.name}</span>
						</p>
					) : null}
					<div className="contact-links">
						{linkedinUrl ? (
							<ActionMenu
								buttonIcon={
									<img
										src="/ats/icons/external-application-linkedin-btn.svg"
										alt="LinkedIn"
									/>
								}
								tooltipText={linkedinUrl}
								tooltipPosition="left"
								items={[
									{
										label: 'Copy LinkedIn URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(linkedinUrl),
										successLabel: 'LinkedIn URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open LinkedIn URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(linkedinUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="contact-icon is-inactive">
								<img
									src="/ats/icons/external-application-linkedin-btn.svg"
									alt="LinkedIn"
								/>
							</div>
						)}
						<div className="contact-icon is-inactive">
							<img
								src="/ats/icons/external-application-phone.svg"
								alt="Phone"
							/>
						</div>
						{data?.email ? (
							<ActionMenu
								buttonIcon={
									<img
										src="/ats/icons/external-application-email.svg"
										alt="Email"
									/>
								}
								tooltipText={data.email}
								tooltipPosition="left"
								items={[
									{
										label: 'Copy email',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data.email),
										successLabel: 'Email copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Send email',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(`mailto:${data.email}`)
									}
								]}
							/>
						) : (
							<div className="contact-icon is-inactive">
								<img
									src="/ats/icons/external-application-email.svg"
									alt="Email"
								/>
							</div>
						)}
					</div>
				</div>
				{isPending ? (
					<div className="external-application-actions">
						<button
							type="button"
							className="external-application-reject-btn"
							disabled={deciding}
							onClick={openRejectModal}>
							<span>Reject</span>
							<img
								src="/ats/icons/external-application-thumbs-down.svg"
								alt=""
							/>
						</button>
						<HoverMenu
							position="left"
							buttonText="Approve"
							buttonClassName="external-application-approve-btn"
							buttonIcon={
								<img
									src="/ats/icons/external-application-thumbs-up.svg"
									alt=""
								/>
							}
							menuClassName="external-application-approve-menu"
							disabled={deciding}
							items={approveMenuItems}
						/>
					</div>
				) : null}
			</div>

			{hasDedupMatch ? (
				<div
					className={`existing-candidate-banner ${
						data?.dedup?.result === 'conflict' ? 'is-conflict' : ''
					}`}>
					<div>
						{data?.dedup?.result === 'conflict'
							? 'This applicant matches two existing candidates. Resolve the duplicate before approving. Please, contact support.'
							: 'This applicant already exists in MIRA.'}
					</div>
					<div className="existing-candidate-links">
						{dedupApplicants.map(applicant => (
							<button
								key={applicant.id}
								type="button"
								className="existing-candidate-link"
								onClick={() => openApplicantProfile(applicant.id)}>
								See {applicant.name || 'profile'}
							</button>
						))}
					</div>
				</div>
			) : null}

			<div className="applied-to-block">
				<span className="applied-to-label">Applied to:</span>
				<span className="applied-to-value">{getAppliedToLabel(job)}</span>
			</div>

			{data?.referral?.comments ? (
				<div className="referral-comments-block">
					<span className="referral-comments-label">
						Referrer additional comments:
					</span>
					<p className="referral-comments-value">{data.referral.comments}</p>
				</div>
			) : null}

			<div className="external-application-resume">
				<FileViewer
					src={resumeUrl}
					mimeType={resumeMimeType}
					fileName={data?.resumeFileName}
					loading={resumeState.loading}
					error={resumeState.error}
					onRetry={() => loadResume(applicationId, data)}
				/>
			</div>
		</div>
	);
}
